import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Home, Plus, TrendingUp, Crown, Menu, ChevronLeft, ChevronRight, HelpCircle, HeartPulse } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { hapticFeedback, shareContent } from '../utils/mobile';
import ErrorBoundary from './ErrorBoundary';
import DarkModeToggle from './DarkModeToggle';
import SideNavigation from './SideNavigation';
import FoodCapture from './FoodCapture';
import WaterTracker from './WaterTracker';
import BadgesDisplay from './BadgesDisplay';
import SubscriptionPage from './SubscriptionPage';
import WeeklyReport from './WeeklyReport';
import WeightTracker from './WeightTracker';
import RecipeSaver from './RecipeSaver';
import MealRecommendations from './MealRecommendations';
import EndOfDaySummary from './EndOfDaySummary';
import ExerciseTracker from './ExerciseTracker';
import AINutritionistChat from './AINutritionistChat';
import RestaurantFinder from './RestaurantFinder';
import FoodDatabaseSearch from './FoodDatabaseSearch';
import BarcodeScanner from './BarcodeScanner';
import ShareToSocial from './ShareToSocial';
import AIMealPlanner from './AIMealPlanner';
import AIRecipeGenerator from './AIRecipeGenerator';
import DietRecipesHub from './DietRecipesHub';
import WearableIntegration from './WearableIntegration';
import AIExerciseCoach from './AIExerciseCoach';
import LegalPages from './LegalPages';
import ExerciseLibrary from './ExerciseLibrary';
import WorkoutPlanner from './WorkoutPlanner';
import WorkoutPrograms from './WorkoutPrograms';
import LiveCoaching from './LiveCoaching';
import ProgressPhotos from './ProgressPhotos';
import BlogHub from './BlogHub';
import ReferralProgram from './ReferralProgram';
import LinkedAccountsManager from './LinkedAccountsManager';
import TwoFactorAuth from './TwoFactorAuth';
import AcquisitionAnalytics from './AcquisitionAnalytics';
import AdminFunnelDashboard from './AdminFunnelDashboard';
import EmailPreferences from './EmailPreferences';
import NotificationBell from './NotificationBell';
import PushNotificationPreferences from './PushNotificationPreferences';
import VideoGenerator from './VideoGenerator';
import HowItWorks from './HowItWorks';
import MyPlan from './MyPlan';
import FeatureTour from './FeatureTour';
import OnboardingTutorial from './OnboardingTutorial';
import AccessGate from './AccessGate';
import WorkoutWizard from './WorkoutWizard';
import SessionLogger from './SessionLogger';
import StreaksWidget from './StreaksWidget';
import ReminderSettings from './ReminderSettings';
import WeightUnitToggle from './WeightUnitToggle';
import LanguageSelector from './LanguageSelector';
import MicronutrientTracker from './MicronutrientTracker';
import AdaptiveCalorieTarget from './AdaptiveCalorieTarget';
import CoachingNudges from './CoachingNudges';
import VoiceCommandButton from './VoiceCommandButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [meals, setMeals] = useState([]);
  const [view, setView] = useState('home');
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showFeatureTour, setShowFeatureTour] = useState(false);
  const [showMenuHint, setShowMenuHint] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const { user, token, logout, fetchUser } = useAuth();
  const { t } = useTranslation();
  
  const contentRef = useRef(null);

  const triggerHaptic = (type = 'light') => {
    hapticFeedback(type === 'success' || type === 'error' ? 'medium' : type);
    if ('vibrate' in navigator) {
      const patterns = { light: 10, medium: 25, heavy: 50, success: [10, 50, 10], error: [50, 30, 50] };
      navigator.vibrate(patterns[type] || 10);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchMeals();
    // Onboarding tutorial / feature tour auto-popups removed — users prefer a clean entry.
    // The help (?) button in the header still lets them replay the tutorial on demand.
  }, []);
  
  useEffect(() => {
    // Menu hint auto-popup also removed for the same reason.
    setShowMenuHint(false);
  }, []);
  
  useEffect(() => {
    if (mobileMenuOpen && showMenuHint) {
      setShowMenuHint(false);
      localStorage.setItem('nutrifitness_menu_hint_seen', 'true');
    }
  }, [mobileMenuOpen, showMenuHint]);
  
  const completeTutorial = () => {
    localStorage.setItem('nutrifitness_onboarding_v2', 'true');
    setShowTutorial(false);
    triggerHaptic('success');
    // Feature tour auto-chain also removed — users can replay from the help (?) button.
  };

  const completeFeatureTour = () => {
    localStorage.setItem('nutrifitness_feature_tour', 'true');
    setShowFeatureTour(false);
    triggerHaptic('success');
  };

  const replayTutorial = () => {
    setShowTutorial(true);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([fetchStats(), fetchMeals(), fetchUser()]);
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMeals = async () => {
    try {
      const response = await axios.get(`${API}/meals/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeals(response.data.meals);
    } catch (error) {
      console.error('Error fetching meals:', error);
    }
  };

  const handleMealCapture = () => {
    fetchStats();
    fetchMeals();
    fetchUser();
    setView('home');
  };

  const handleUpgrade = () => {
    fetchUser();
    setView('home');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const caloriePercentage = stats?.consumed_calories && stats?.daily_calorie_target ? (stats.consumed_calories / stats.daily_calorie_target) * 100 : 0;
  const macroData = stats ? [
    { name: 'Protein', value: stats.protein_consumed || 0, color: '#ef4444' },
    { name: 'Carbs', value: stats.carbs_consumed || 0, color: '#f59e0b' },
    { name: 'Fats', value: stats.fats_consumed || 0, color: '#eab308' }
  ] : [];

  const isPremium = user?.subscription_plan !== 'free';
  const dailyScansLeft = stats?.subscription_plan === 'free' ? Math.max(0, 5 - stats.meals_today) : -1;

  return (
    <AccessGate>
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 overflow-x-hidden w-full max-w-full">
      {/* Onboarding Tutorial */}
      {showTutorial && (
        <OnboardingTutorial onComplete={completeTutorial} />
      )}

      {/* Interactive Feature Tour */}
      {showFeatureTour && !showTutorial && (
        <FeatureTour onComplete={completeFeatureTour} />
      )}

      {/* Header */}
      <header className="bg-black dark:bg-gray-900 shadow-sm fixed top-0 left-0 right-0 z-[9999] transition-colors duration-300" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-[1168px] mx-auto px-4 py-2">
          <div className="lg:hidden flex items-center justify-between relative h-[80px]">
            <img src="/logo-v4.png" alt="Logo" className="h-16 w-16 object-contain" />
            <div className="absolute left-1/2 -translate-x-1/2">
              <button
                data-testid="mobile-menu-btn"
                onClick={() => { triggerHaptic('medium'); setMobileMenuOpen(true); }}
                className="bg-transparent p-3 rounded-lg flex flex-col justify-center items-center gap-1.5 active:opacity-60 focus:outline-none focus:bg-transparent hover:bg-transparent"
                aria-label="Open menu"
              >
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={replayTutorial} className="p-2 rounded-full bg-transparent hover:bg-transparent active:opacity-60 transition-opacity" data-testid="help-btn" aria-label="Help">
                <HelpCircle className="w-5 h-5 text-[#FF6600]" />
              </button>
              <NotificationBell token={token} />
              <DarkModeToggle variant="nav" />
            </div>
          </div>
          <div className="hidden lg:flex items-center justify-between relative">
            <img src="/logo-v4.png" alt="Logo" className="h-20 w-20 object-contain" />
            <div className="absolute left-1/2 -translate-x-1/2">
              <button
                data-testid="desktop-menu-btn"
                onClick={() => { triggerHaptic('medium'); setMobileMenuOpen(true); }}
                className="bg-transparent p-3 rounded-lg transition-all flex flex-col justify-center items-center gap-1.5 active:opacity-60"
                aria-label="Open menu"
              >
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={replayTutorial} className="p-2 rounded-full hover:bg-gray-800/50 transition-colors" data-testid="help-btn-desktop" aria-label="Help">
                <HelpCircle className="w-5 h-5 text-gray-300" />
              </button>
              <NotificationBell token={token} />
              <DarkModeToggle variant="nav" />
              {!isPremium && (
                <button
                  data-testid="upgrade-btn"
                  onClick={() => setView('subscription')}
                  className="bg-[#FF6600] hover:bg-[#E55C00] text-white px-4 py-2 rounded-full flex items-center gap-2 text-sm font-semibold transition-colors"
                >
                  <Crown size={16} />
                  Upgrade
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="h-[100px] md:h-[100px]" style={{ minHeight: 'calc(100px + env(safe-area-inset-top, 0px))' }}></div>

      <SideNavigation
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        user={user}
        isPremium={isPremium}
        currentView={view}
        onNavigate={(viewId) => setView(viewId)}
        onLogout={logout}
      />

      <div ref={contentRef} className="max-w-[1168px] mx-auto px-4 pb-32 md:pb-8 w-full" style={{ paddingTop: '16px' }}>
        {view === 'home' && stats && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Streaks & Achievements Widget */}
            <div className="lg:col-span-2" data-tour="streaks-widget">
              <StreaksWidget token={token} />
            </div>

            {/* AI Coaching Nudges */}
            <div className="lg:col-span-2" data-tour="coaching-nudges">
              <CoachingNudges token={token} onAction={(v) => setView(v)} />
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
              <h2 className="text-lg font-bold mb-3">{t('dashboard.todayProgress')}</h2>
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">Calories</span>
                  <span className="font-semibold" data-testid="calorie-display">
                    {Math.round(stats.consumed_calories)} / {Math.round(stats.adaptive_calorie_target || stats.daily_calorie_target)}
                  </span>
                </div>
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#FF6600] to-[#FF8533] transition-all" style={{ width: `${Math.min(caloriePercentage, 100)}%` }} />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{Math.round(stats.remaining_calories)} calories remaining</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center">
                  <div className="text-red-500 font-bold">{Math.round(stats.protein_consumed)}g</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Protein</div>
                </div>
                <div className="text-center">
                  <div className="text-yellow-500 font-bold">{Math.round(stats.carbs_consumed)}g</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Carbs</div>
                </div>
                <div className="text-center">
                  <div className="text-amber-500 font-bold">{Math.round(stats.fats_consumed)}g</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Fats</div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
              <h2 className="text-lg font-bold mb-3">Macro Distribution</h2>
              <ErrorBoundary>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={macroData} cx="50%" cy="50%" labelLine={false} label={(entry) => `${entry.name}: ${Math.round(entry.value)}g`} outerRadius={70} fill="#8884d8" dataKey="value">
                      {macroData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </ErrorBoundary>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
              <h2 className="text-lg font-bold mb-3">Weekly Progress</h2>
              <ErrorBoundary>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={stats.weekly_progress || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{fontSize: 12}} />
                    <YAxis tick={{fontSize: 12}} />
                    <Tooltip />
                    <Legend wrapperStyle={{fontSize: 12}} />
                    <Line type="monotone" dataKey="calories" stroke="#f97316" strokeWidth={2} name="Consumed" />
                    <Line type="monotone" dataKey="target" stroke="#6366f1" strokeWidth={2} strokeDasharray="5 5" name="Target" />
                  </LineChart>
                </ResponsiveContainer>
              </ErrorBoundary>
            </div>
          </div>
        )}

        {view === 'capture' && (
          <div className="max-w-2xl mx-auto">
            <FoodCapture onCapture={handleMealCapture} token={token} dailyScansLeft={dailyScansLeft} />
          </div>
        )}

        {view === 'history' && (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
            <h2 className="text-lg font-bold mb-4">Meal History</h2>
            <div className="space-y-3">
              {meals.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 text-sm py-8">No meals logged yet</p>
              ) : (
                meals.map((meal) => (
                  <div key={meal.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex gap-3" data-testid={`meal-card-${meal.id}`}>
                    <img src={meal.image_base64} alt="Meal" className="w-16 h-16 object-cover rounded-lg flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-1">
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-semibold text-sm capitalize">{meal.meal_type}</h3>
                          {meal.analysis.dish_count > 1 && (
                            <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">{meal.analysis.dish_count} dishes</span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{new Date(meal.timestamp).toLocaleDateString()}</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        <div><div className="text-gray-500 dark:text-gray-400">Cal</div><div className="font-medium">{Math.round(meal.analysis.total_calories)}</div></div>
                        <div><div className="text-gray-500 dark:text-gray-400">Prot</div><div className="font-medium">{Math.round(meal.analysis.total_protein)}g</div></div>
                        <div><div className="text-gray-500 dark:text-gray-400">Carb</div><div className="font-medium">{Math.round(meal.analysis.total_carbs)}g</div></div>
                        <div><div className="text-gray-500 dark:text-gray-400">Fat</div><div className="font-medium">{Math.round(meal.analysis.total_fats)}g</div></div>
                      </div>
                      {meal.analysis.meal_tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {meal.analysis.meal_tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded-full">{tag}</span>
                          ))}
                        </div>
                      )}
                      <details className="text-xs mt-2">
                        <summary className="cursor-pointer text-[#FF6600]">View items ({meal.analysis.food_items.length})</summary>
                        <ul className="mt-1 space-y-1 text-gray-600 dark:text-gray-300">
                          {meal.analysis.food_items.map((item, idx) => (
                            <li key={idx} className="flex items-center justify-between">
                              <span>{item.name}</span>
                              <span className="text-gray-400 flex items-center gap-1">
                                {item.portion_grams && <span>{item.portion_grams}g</span>}
                                <span>{Math.round(item.calories)} cal</span>
                                {item.confidence && <span className={`w-1.5 h-1.5 rounded-full ${item.confidence === 'high' ? 'bg-green-400' : item.confidence === 'medium' ? 'bg-yellow-400' : 'bg-red-400'}`} title={`${item.confidence} confidence`} />}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {view === 'badges' && <BadgesDisplay token={token} />}
        {view === 'recommendations' && (<div className="space-y-4"><MealRecommendations token={token} /><EndOfDaySummary token={token} /></div>)}
        {view === 'exercise' && <ExerciseTracker token={token} />}
        {view === 'exercise-coach' && <AIExerciseCoach token={token} />}
        {view === 'workout-planner' && <WorkoutPlanner token={token} />}
        {view === 'workout-programs' && <WorkoutPrograms token={token} />}
        {view === 'exercise-library' && <ExerciseLibrary token={token} />}
        {view === 'workout-wizard' && <WorkoutWizard token={token} onNavigate={(v) => setView(v)} />}
        {view === 'session-logger' && <SessionLogger token={token} onNavigate={(v) => setView(v)} />}
        {view === 'live-coaching' && <LiveCoaching token={token} />}
        {view === 'progress-photos' && <ProgressPhotos token={token} />}
        {view === 'ai-chat' && <AINutritionistChat token={token} />}
        {view === 'ai-meal-planner' && <AIMealPlanner token={token} />}
        {view === 'diet-recipes' && <DietRecipesHub token={token} />}
        {view === 'restaurants' && <RestaurantFinder token={token} />}
        {view === 'food-database' && <FoodDatabaseSearch token={token} />}
        {view === 'barcode' && <BarcodeScanner token={token} />}
        {view === 'social' && <ShareToSocial token={token} />}
        {view === 'wearables' && <WearableIntegration token={token} />}
        {view === 'analytics' && (<div className="space-y-24"><WeeklyReport token={token} /><WeightTracker token={token} /></div>)}
        {view === 'micronutrients' && <MicronutrientTracker token={token} />}
        {view === 'adaptive-target' && <AdaptiveCalorieTarget token={token} onTargetChanged={fetchStats} />}
        {view === 'premium' && (
          <div className="space-y-6">
            <WaterTracker token={token} />
            {isPremium && <WeightTracker token={token} />}
            {!isPremium && (
              <div className="bg-orange-50 border-2 border-[#FF6600] rounded-xl p-6 text-center max-w-md mx-auto">
                <Crown size={32} className="mx-auto mb-3 text-[#FF6600]" />
                <h2 className="text-xl font-bold mb-2">Unlock Premium Features</h2>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">Get unlimited scans, meal planning, and advanced analytics</p>
                <button onClick={() => setView('subscription')} className="bg-[#FF6600] hover:bg-[#E55C00] text-white px-6 py-2 rounded-lg font-semibold text-sm transition-colors">View Plans</button>
              </div>
            )}
          </div>
        )}
        {view === 'recipes' && isPremium && <RecipeSaver token={token} />}
        {view === 'referral' && <ReferralProgram token={token} />}
        {view === 'blog' && <BlogHub token={token} />}
        {view === 'acquisition-analytics' && <AcquisitionAnalytics token={token} />}
        {view === 'admin-funnel' && <AdminFunnelDashboard onBack={() => setView('home')} />}
        {view === 'video-studio' && <VideoGenerator token={token} />}
        {view === 'how-it-works' && <HowItWorks />}
        {view === 'my-plan' && <MyPlan token={token} />}
        {view === 'legal-terms' && <LegalPages page="terms-of-service" onBack={() => setView('home')} />}
        {view === 'legal-privacy' && <LegalPages page="privacy-policy" onBack={() => setView('home')} />}
        {view === 'legal-health' && <LegalPages page="health-disclaimer" onBack={() => setView('home')} />}
        {view === 'legal-ai' && <LegalPages page="ai-disclaimer" onBack={() => setView('home')} />}
        {view === 'account' && (
          <div className="space-y-6 max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('settings.title')}</h2>
            <LanguageSelector />
            <WeightUnitToggle token={token} />
            <PushNotificationPreferences token={token} />
            <ReminderSettings token={token} />
            <EmailPreferences />
            <TwoFactorAuth onUpdate={fetchUser} />
            <LinkedAccountsManager onUpdate={fetchUser} />
            
            {/* Delete Account Section - Prominent for App Store compliance */}
            <div className="mt-8 p-6 bg-red-50 dark:bg-red-950 border-2 border-red-300 dark:border-red-800 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600 dark:text-red-400"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-red-700 dark:text-red-400 mb-1">Delete Account</h3>
                  <p className="text-sm text-red-600/80 dark:text-red-300/80 mb-4">
                    Permanently delete your account and all associated data including meals, exercises, progress photos, and personal information. This action cannot be undone.
                  </p>
                  <button
                    data-testid="delete-account-btn"
                    onClick={() => {
                      setDeleteConfirmText('');
                      setShowDeleteModal(true);
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold text-sm transition-colors shadow-sm"
                  >
                    Delete My Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {view === 'subscription' && (
          <SubscriptionPage token={token} currentPlan={user?.subscription_plan || 'free'} onUpgrade={handleUpgrade} />
        )}
      </div>

      {/* Legal Footer */}
      <footer className="py-4 px-4 bg-gray-100 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 mt-auto">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <button onClick={() => setView('legal-terms')} className="hover:text-[#FF6600] transition-colors" data-testid="dash-footer-terms">Terms of Service</button>
          <span>|</span>
          <button onClick={() => setView('legal-privacy')} className="hover:text-[#FF6600] transition-colors" data-testid="dash-footer-privacy">Privacy Policy</button>
          <span>|</span>
          <button onClick={() => setView('legal-health')} className="hover:text-[#FF6600] transition-colors" data-testid="dash-footer-health">Health Disclaimer</button>
          <span>|</span>
          <button onClick={() => setView('legal-ai')} className="hover:text-[#FF6600] transition-colors" data-testid="dash-footer-ai">AI Disclaimer</button>
        </div>
      </footer>
      <VoiceCommandButton token={token} onNavigate={(page) => setView(page)} />

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[10000] bg-black/80 flex items-center justify-center p-4" data-testid="delete-account-modal">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-sm w-full p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-red-700 dark:text-red-400 mb-2">Delete Account</h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              This action is <strong>permanent</strong>. All your meals, exercises, progress, photos and personal data will be deleted forever.
            </p>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Type <span className="text-red-600 font-bold">DELETE</span> to confirm:
            </label>
            <input
              type="text"
              autoFocus
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              data-testid="delete-confirm-input"
              className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:border-red-500 focus:outline-none text-gray-900 dark:text-white dark:bg-gray-700 font-medium mb-4"
              placeholder="DELETE"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="flex-1 py-2.5 border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
                data-testid="delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await axios.delete(`${API}/auth/account`, { headers: { Authorization: `Bearer ${token}` } });
                    localStorage.removeItem('token');
                    window.location.reload();
                  } catch (err) {
                    alert('Failed to delete account. Please try again.');
                    setDeleting(false);
                  }
                }}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                data-testid="delete-confirm-btn"
              >
                {deleting ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </AccessGate>
  );
};

export default Dashboard;
